import React, { useCallback, useEffect } from "react";
import { Badge, Drawer, Grid, Popover, Spin, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ReactComponent as BellIcon } from "../../Assets/Icons/bell-2.svg";
import {
  closeNotifications,
  getNotifications,
  getUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
  openNotifications,
} from "../../Redux/Slices/NotificationSlice";
import "./index.scss";

const POLL_INTERVAL_MS = 60000;

// * Written inline rather than pulled from moment: the employer app does not
// * have moment, and this component is ported there as is. * //
export const formatRelativeTime = (value) => {
  if (!value) return "";

  const seconds = Math.floor((Date.now() - new Date(value).getTime()) / 1000);

  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;

  return new Date(value).toLocaleDateString();
};

const NotificationList = () => {
  const { notifications, loading, unreadCount } = useSelector(
    (state) => state.NotificationSlice
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleOpen = (notification) => {
    if (!notification.read) {
      dispatch(markNotificationRead({ id: notification.id }));
    }
    dispatch(closeNotifications());

    if (notification.linkUrl) {
      navigate(notification.linkUrl);
    }
  };

  return (
    <div className="notification-panel">
      <div className="notification-panel-header">
        <Typography className="notification-panel-title">
          Notifications
        </Typography>
        {unreadCount > 0 && (
          <Typography
            className="notification-mark-all cursor"
            onClick={() => dispatch(markAllNotificationsRead())}
          >
            Mark all as read
          </Typography>
        )}
      </div>

      <div className="notification-panel-body">
        {loading && notifications.length === 0 && (
          <div className="notification-empty">
            <Spin />
          </div>
        )}

        {!loading && notifications.length === 0 && (
          <div className="notification-empty">
            <Typography className="notification-empty-text">
              You have no notifications yet
            </Typography>
          </div>
        )}

        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`notification-item cursor ${
              notification.read ? "" : "notification-item-unread"
            }`}
            onClick={() => handleOpen(notification)}
          >
            <div className="notification-item-dot-wrapper">
              {!notification.read && <span className="notification-dot" />}
            </div>
            <div className="notification-item-content">
              <Typography className="notification-item-title">
                {notification.title}
              </Typography>
              <Typography className="notification-item-body">
                {notification.body}
              </Typography>
              <Typography className="notification-item-time">
                {formatRelativeTime(notification.createdAt)}
              </Typography>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Notifications = () => {
  const { unreadCount, open } = useSelector((state) => state.NotificationSlice);
  const dispatch = useDispatch();
  const { useBreakpoint } = Grid;
  const { md } = useBreakpoint();
  const isMobile = md === false;

  const refreshCount = useCallback(() => {
    if (!localStorage.getItem("token")) return;
    if (document.visibilityState === "hidden") return;
    dispatch(getUnreadCount());
  }, [dispatch]);

  // * Polled rather than pushed - there is no websocket layer. Skipped while
  // * the tab is hidden so a backgrounded tab is not still calling. * //
  useEffect(() => {
    refreshCount();
    const timer = setInterval(refreshCount, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", refreshCount);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshCount);
    };
  }, [refreshCount]);

  const handleOpenChange = (nextOpen) => {
    if (nextOpen) {
      dispatch(getNotifications({ page: 1 }));
      dispatch(openNotifications());
    } else {
      dispatch(closeNotifications());
    }
  };

  const bell = (
    <div className="notification-bell cursor">
      <Badge count={unreadCount} size="small" offset={[-2, 2]}>
        <BellIcon />
      </Badge>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <div onClick={() => handleOpenChange(true)}>{bell}</div>
        <Drawer
          placement="bottom"
          height="85%"
          open={open}
          onClose={() => handleOpenChange(false)}
          className="notification-drawer"
          title={null}
          closable={false}
          styles={{
            content: { borderRadius: "16px 16px 0 0" },
            body: {
              padding: 0,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            },
          }}
        >
          <NotificationList />
        </Drawer>
      </>
    );
  }

  return (
    <Popover
      content={<NotificationList />}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={handleOpenChange}
      overlayClassName="notification-popover"
    >
      {bell}
    </Popover>
  );
};

export default Notifications;
